/**
 * "This needs the Vault" for secrets that used to live in the OS keychain
 * (git account tokens, extension secureStorage — owner, 26.10.2026: the app
 * keeps everything in its own storage, macOS must not prompt for a keychain).
 *
 * Rust answers `vault_locked` (exists, locked) or `vault_needs_bootstrap`
 * (not set up yet) before it touches anything. A user-started action opens
 * the always-mounted Vault gate, waits for it, and tries once more; the gate
 * itself picks unlock or setup. Background work never opens the gate: it
 * checks `isVaultLockedError` and shows a quiet "Unlock the Vault" state
 * instead (the SSH/FTP/DB rows already do the same).
 *
 * vaultStore is imported on use: it pulls the vault sync and chat-share
 * modules, which callers like gitAccounts.js must not drag into every test.
 */

/** What a git sync says while the Vault holding its token is locked. */
export const VAULT_LOCKED_SYNC_MESSAGE = 'Unlock the Vault to sync';

const GATE_TOKENS = new Set(['vault_locked', 'vault_needs_bootstrap', 'passphrase_required']);

/** True when `e` means "unlock (or create) the Vault first". */
export function isVaultLockedError(e) {
  if (e?.code && GATE_TOKENS.has(e.code)) return true;
  const raw = (typeof e === 'string' ? e : String(e?.message || e || '')).trim();
  return GATE_TOKENS.has(raw);
}

/** A typed error the UI can recognise, for code that turns a lock into a throw. */
export function vaultLockedError(code = 'vault_locked') {
  const err = new Error(code);
  err.code = code;
  return err;
}

let pending = null;
const GATE_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Open the Vault gate and resolve true after an unlock (or setup), false when
 * the user closes it. Concurrent callers share one gate.
 */
export function waitForVaultGate(reason = 'vault') {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (pending) return pending;
  // Only Rust answers vault_locked, so this only runs in the desktop app,
  // where the gate host is mounted at the root.
  pending = import('../store/vaultStore')
    .then(
      ({ openVaultGate, VAULT_GATE_UNLOCKED_EVENT, VAULT_GATE_DISMISSED_EVENT }) =>
        new Promise((resolve) => {
          let timer = null;
          const done = (ok) => {
            clearTimeout(timer);
            window.removeEventListener(VAULT_GATE_UNLOCKED_EVENT, onOk);
            window.removeEventListener(VAULT_GATE_DISMISSED_EVENT, onNo);
            resolve(ok);
          };
          // A gate nobody answers (no host mounted) must not hold every later
          // secret call hostage: give up after a generous while.
          timer = setTimeout(() => done(false), GATE_TIMEOUT_MS);
          const onOk = () => done(true);
          const onNo = () => done(false);
          window.addEventListener(VAULT_GATE_UNLOCKED_EVENT, onOk);
          window.addEventListener(VAULT_GATE_DISMISSED_EVENT, onNo);
          openVaultGate(reason);
        })
    )
    .finally(() => {
      pending = null;
    });
  return pending;
}

/**
 * Run `work`; when it fails because the Vault is locked or not set up, open
 * the gate and run it once more after an unlock. A dismissed gate rethrows
 * the original error. For user-started actions only.
 */
export async function withVaultUnlocked(work, reason = 'vault') {
  try {
    return await work();
  } catch (e) {
    if (!isVaultLockedError(e)) throw e;
    const unlocked = await waitForVaultGate(reason);
    if (!unlocked) throw e;
    return work();
  }
}
